import { Link } from 'react-router-dom'
import { useLangue, useTextes } from '../LangueContexte'
import { useVille } from '../VilleContexte'
import { Icone, Image } from '../ui'
import { montant, nombre } from '../formats'
import type { Vignette as Annonce } from '../apiRecherche'
import type { MatchAnnonce } from '../apiMatching'

/**
 * Les deux badges, rendus **séparément** — invariant 7.
 *
 * Ils ne partagent ni pictogramme, ni couleur, ni libellé, et c'est délibéré. « Un badge = une
 * promesse = une seule source » : si l'interface les rendait de la même façon, la distinction que
 * le modèle protège si soigneusement disparaîtrait là où elle compte — à l'écran.
 *
 * Les emoji ont disparu (§6, règle 3) : 📷 et ✅ se rendaient différemment sur chaque plateforme,
 * se faisaient lire à voix haute, et ne se coloraient pas. Le second était en outre un ✅ vert
 * générique là où le badge dit « quelqu'un s'est déplacé ».
 */
export function Badges({ photos, verifiee }: { photos: boolean; verifiee: boolean }) {
  const t = useTextes()

  if (!photos && !verifiee) {
    return null
  }

  return (
    <span className="badges">
      {photos && (
        <span className="badge badge--photos" title={t.badgePhotosAide}>
          <Icone nom="appareil" taille="sm" />
          <span className="badge__libelle">{t.badgePhotos}</span>
        </span>
      )}
      {verifiee && (
        <span className="badge badge--verifiee" title={t.badgeVerifieeAide}>
          <Icone nom="verifie" taille="sm" />
          <span className="badge__libelle">{t.badgeVerifiee}</span>
        </span>
      )}
    </span>
  )
}

/**
 * Une annonce dans une liste de résultats — anatomie du §5.5.
 *
 * **Ni bordure, ni ombre au repos : la photo porte la carte.** L'ombre n'apparaît qu'au survol, et
 * seulement là où il existe un survol. C'est le composant qui décide de l'allure de l'écran le
 * plus utilisé de l'application.
 *
 * **Toute la carte est UN SEUL lien**, posé en surimpression, et le cœur est le seul élément
 * interactif superposé. Un `<button>` qui appelle `navigate` casserait le clic du milieu,
 * l'ouverture dans un nouvel onglet et la copie du lien — pour un gain nul.
 *
 * **Une seule photo**, décision du 07/09/2026 : le carrousel par carte supposait que la recherche
 * renvoie plusieurs médias, donc une évolution de `ListingCardDto`. Elle a été écartée ; la carte
 * s'en tient à la couverture, et la boîte à ratio fixe garde l'alignement de la grille.
 */
export function Vignette({
  annonce,
  recommandee = null,
  avecVille = false,
  onFavori,
  onSurvol,
}: {
  annonce: Annonce
  /** L'annonce est dans le fil de la personne connectée — ticket `fo-recherche-16`. Arrive en différé. */
  recommandee?: MatchAnnonce | null
  /**
   * La ville sur la carte (L13) — SEULEMENT là où la liste peut en mélanger : favoris, nouveautés.
   * Jamais sur les résultats de recherche, où l'en-tête la porte et où elle ne varie pas.
   */
  avecVille?: boolean
  onFavori?: (id: string, actuel: boolean) => void
  /** Survol croisé liste ⇄ carte (§5 bis.2) : c'est ce qui fait des deux panneaux un seul outil. */
  onSurvol?: (id: string | null) => void
}) {
  const t = useTextes()
  const langue = useLangue()
  const ville = useVille()
  const lieu = avecVille
    ? `${annonce.district}, ${ville.libelle(annonce.cityCode) || annonce.city}`
    : annonce.district
  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })

  // Une annonce qui n'est plus à prendre n'est recommandée à personne : sur un favori loué, c'est
  // la mention « retirée du marché » qui s'affiche, jamais les deux.
  const estRecommandee = annonce.isAvailable && recommandee !== null
  const nombreDeTags =
    (estRecommandee ? 1 : 0) + (annonce.hasOriginalPhotos ? 1 : 0) + (annonce.isVerifiedOnSite ? 1 : 0)

  const NATURES: Record<Annonce['unitKind'], string> = {
    EntireProperty: t.typeLogementEntier,
    PrivateRoom: t.typeChambrePrivee,
    SharedRoom: t.typeChambrePartagee,
  }

  return (
    <li
      className={annonce.isAvailable ? 'annonce' : 'annonce annonce--retiree'}
      onMouseEnter={onSurvol === undefined ? undefined : () => onSurvol(annonce.id)}
      onMouseLeave={onSurvol === undefined ? undefined : () => onSurvol(null)}
    >
      <div className="annonce__media">
        <Image
          mediaId={annonce.coverMediaId}
          alt=""
          ratio="1-1"
          /* La grille tient quatre colonnes au-delà de 82rem, deux en dessous, une en compact :
             le navigateur choisit la déclinaison sur cette base, et non sur 100vw. */
          sizes="(min-width: 82rem) 20rem, (min-width: 40rem) 45vw, 92vw"
          vide={t.annonceSansPhoto}
        />

        {/*
          **Les tags de la carte, en haut au début de ligne** — ticket `fo-recherche-16`, réouverture
          du 11/09.

          « Recommandée pour vous » d'abord, puis les deux badges du bien. Dès que DEUX tags se
          partagent le coin, chacun se replie sur son icône et le survol déplie celui qu'on vise,
          les autres restant en icône : trois libellés côte à côte couvraient la photo. Un tag
          seul garde son libellé — une icône seule ne dit rien à qui ne la connaît pas déjà.

          L'étiquette est sur la photo et non dans le corps : elle arrive APRÈS la carte, en
          différé, et une ligne qui apparaîtrait sous le prix ferait sauter toute la grille (règle
          11 du §6 de L10). Ni la couleur ni la forme des deux badges du bien (invariant 7) :
          ceux-là parlent du bien, celle-ci parle de vous — les confondre ferait lire une
          recommandation comme une garantie. Le score reste en info-bulle : la vignette dit un
          mot, la fiche explique.
        */}
        <span className={nombreDeTags > 1 ? 'annonce__badges annonce__badges--compact' : 'annonce__badges'}>
          {estRecommandee && recommandee !== null && (
            <span
              className="badge badge--recommandee"
              title={`${t.badgeRecommandeeAide} ${pourcent.format(recommandee.score)}`}
            >
              <Icone nom="etoile" taille="sm" />
              <span className="badge__libelle">{t.badgeRecommandee}</span>
            </span>
          )}
          <Badges photos={annonce.hasOriginalPhotos} verifiee={annonce.isVerifiedOnSite} />
        </span>

        {/*
          **Retirée du marché : grisée, et dite** — ticket `fo-favoris-01`.

          Le gris seul ne suffit pas : il se lit comme un défaut d'affichage, et il ne se lit pas
          du tout par un lecteur d'écran. La pastille porte le mot, et le gris porte le coup d'œil.
          L'annonce reste cliquable — on garde le droit de rouvrir ce qu'on avait mis de côté.
        */}
        {!annonce.isAvailable && (
          <span className="annonce__retiree">{t.favoriRetireeDuMarche}</span>
        )}


        {/*
          **Combien de photos, sur la couverture** — ticket `fo-annonce-11`.

          Une vignette montre une photo et n'annonce pas les autres : on ouvre une fiche pour
          découvrir qu'il n'y en avait qu'une, ou on passe à côté de celle qui en portait
          quatorze. Le nombre est ce qui décide d'ouvrir, et il se lit sans quitter la grille.

          En bas de la couverture, du côté opposé à la mention « retirée du marché » : les deux
          peuvent coexister sur un favori loué, et se chevaucher les rendrait illisibles tous les
          deux. Le cœur, lui, garde le haut.

          Rien à zéro ni à une seule photo : « 1 » ne dit rien de plus que la photo elle-même, et
          « 0 » répéterait ce que la vignette vide montre déjà.
        */}
        {annonce.photoCount > 1 && (
          <span className="annonce__photos" aria-label={`${nombre(annonce.photoCount, langue)} ${t.annoncePhotos}`}>
            <Icone nom="image" taille="sm" />
            {nombre(annonce.photoCount, langue)}
          </span>
        )}

        {onFavori !== undefined && (
          <button
            type="button"
            className="annonce__coeur"
            aria-pressed={annonce.isFavorite}
            aria-label={annonce.isFavorite ? t.favoriRetirer : t.favoriAjouter}
            onClick={() => onFavori(annonce.id, annonce.isFavorite)}
          >
            <Icone nom="coeur" />
          </button>
        )}
      </div>

      <div className="annonce__corps">
        <span className="annonce__titre">{annonce.title}</span>
        <span className="annonce__meta">
          {lieu} · {NATURES[annonce.unitKind]}
          {annonce.bedroomCount > 0 && ` · ${nombre(annonce.bedroomCount, langue)} ${t.annonceChambres}`}
        </span>
        <span className="annonce__prix">
          <bdi>{montant(annonce.rentAllInclusive, annonce.currencyCode, langue)}</bdi>{' '}
          <span>{t.annonceParMois}</span>
        </span>
      </div>

      {/* Le lien couvre la carte entière ; son libellé est le titre, que le lecteur d'écran
          annonce donc une seule fois — la photo est décorative et le titre n'est pas un lien
          séparé. */}
      <Link className="annonce__lien" to={`/annonces/${annonce.id}`} aria-label={annonce.title} />
    </li>
  )
}

/**
 * La carte, pendant le chargement.
 *
 * Elle occupe **exactement** la place de la carte réelle : c'est ce qui fait qu'aucune ligne ne
 * bouge quand les résultats arrivent. Un squelette qui n'a pas la bonne forme déplace la mise en
 * page deux fois au lieu d'une.
 */
export function VignetteSquelette() {
  return (
    <li className="annonce" aria-hidden="true">
      <div className="annonce__media">
        <span className="squelette" style={{ display: 'block', blockSize: '100%' }} />
      </div>
      <div className="annonce__corps">
        <span className="squelette squelette--texte" style={{ inlineSize: '85%' }} />
        <span className="squelette squelette--texte" style={{ inlineSize: '60%' }} />
        <span className="squelette squelette--texte" style={{ inlineSize: '40%' }} />
      </div>
    </li>
  )
}
